<template>
  <div class="card shadow-sm table-card review-card mb-4">
    <div class="form-card-header">
      <div class="section-number"><i class="fe fe-eye" style="font-size: 12px;"></i></div>
      <h6 class="mb-0 font-weight-bold text-white">Review your answers</h6>
      <span class="badge badge-light ml-auto" style="border-radius: 20px;">{{ answeredCount }} of {{ items.length }} answered</span>
    </div>

    <div class="card-body px-4 py-3">
      <div class="progress mb-3" style="height: 6px;" role="progressbar" :aria-valuenow="answeredCount" aria-valuemin="0" :aria-valuemax="items.length">
        <div class="progress-bar bg-success" :style="{ width: (items.length ? (answeredCount / items.length) * 100 : 0) + '%' }"></div>
      </div>
      <div v-if="answeredCount < items.length" class="alert alert-warning small py-2 mb-3">
        <i class="fe fe-alert-triangle mr-1"></i>
        {{ items.length - answeredCount }} question{{ items.length - answeredCount === 1 ? "" : "s" }} not answered. Go back to fill
        {{ items.length - answeredCount === 1 ? "it" : "them" }}, or submit as it is.
      </div>

      <div v-for="g in groups" :key="g.key" class="review-group">
        <div class="group-title">
          {{ g.name }} <span class="group-count">{{ g.items.length }}</span>
        </div>
        <div v-for="(item, idx) in g.items" :key="item.id" class="q-row" :class="{ 'q-row--missing': !answers(item).length }">
          <span class="q-num">{{ idx + 1 }}</span>
          <div class="flex-fill">
            <div class="q-text">{{ item.item_name }}</div>
            <div class="d-flex flex-wrap mt-2" style="gap: 6px;">
              <span v-for="(a, i) in answers(item)" :key="i" class="answer-chip">{{ a }}</span>
              <span v-if="!answers(item).length" class="missing-chip"><i class="fe fe-alert-circle mr-1"></i>Not answered</span>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end flex-wrap mt-4" style="gap: 10px;">
        <button type="button" class="btn btn-light px-4" @click="continueEdit" :disabled="isLoading">
          <i class="fe fe-edit-2 mr-1"></i> Back to editing
        </button>
        <button type="button" class="btn btn-submit px-5" @click="submit" :disabled="isLoading || !answeredCount">
          <span v-if="isLoading" class="spinner-border spinner-border-sm mr-1" role="status"></span>
          <i v-else class="fe fe-send mr-1"></i> Submit
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["qas"],
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    items() {
      return Array.isArray(this.qas) ? this.qas : [];
    },
    // the checklist grouped by section, in form order
    groups() {
      const groups = [];
      const byCat = {};
      this.items.forEach((item) => {
        const cat = item.category || {};
        const key = cat.id || "none";
        if (!byCat[key]) {
          byCat[key] = { key, name: cat.category_name || "Other questions", items: [] };
          groups.push(byCat[key]);
        }
        byCat[key].items.push(item);
      });
      return groups;
    },
    answeredCount() {
      return this.items.filter((i) => this.answers(i).length).length;
    },
  },
  methods: {
    // a multi-select answer is an array; everything else is a single value
    answers(item) {
      const v = item.option_select;
      const list = Array.isArray(v) ? v : [v];
      return list.filter((a) => a !== null && a !== undefined && String(a).trim() !== "").map(String);
    },
    continueEdit() {
      this.$emit("clicked", "edit");
    },
    submit() {
      if (!confirm("Submit this checklist? You can't change the answers afterwards.")) return;
      // send a copy: the checklist itself stays intact if saving fails and the user goes back to edit
      const payload = this.items
        .filter((item) => this.answers(item).length)
        .map((item) => {
          const row = {
            quality_assurance_id: parseInt(this.$route.params.id),
            quality_assurance_item_id: item.id,
            response: item.option_select,
            score: 0,
          };
          if (item.custom_input_value) row.custom_input_value = item.custom_input_value;
          return row;
        });
      this.isLoading = true;
      this.axios
        .post("/api/v1/auth/qa-addResponse", payload)
        .then(() => {
          this.$toasted.success("Checklist submitted", { position: "top-center", duration: 3000 });
          this.$router.push(`/quality-assurance`);
        })
        .catch(() => {
          this.$toasted.error("Could not submit the checklist. Check your connection and try again.", {
            position: "top-center",
            duration: 4000,
          });
        })
        .finally(() => { this.isLoading = false; });
    },
  },
};
</script>

<style scoped>
.review-card { border: none; border-radius: 12px; }

.review-group + .review-group { margin-top: 18px; }
.group-title {
  font-size: .78rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
  color: #475569; padding-bottom: 6px; border-bottom: 2px solid #e2e8f0;
  display: flex; align-items: center; gap: 8px;
}
.group-count { background: #f1f5f9; border-radius: 10px; padding: 0 7px; font-size: .72rem; color: #64748b; }

.q-row { display: flex; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f1f5f9; }
.q-row:last-child { border-bottom: none; }
.q-num {
  display: inline-flex; width: 26px; height: 26px; border-radius: 50%;
  background: #ecfdf5; color: #047857; font-size: .75rem; font-weight: 700;
  align-items: center; justify-content: center; flex-shrink: 0;
}
.q-row--missing .q-num { background: #fff7ed; color: #c2410c; }
.q-text { font-size: .9rem; font-weight: 600; color: #1e293b; }

.answer-chip {
  display: inline-block; background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe;
  border-radius: 20px; padding: 2px 12px; font-size: .84rem; font-weight: 600;
}
.missing-chip {
  display: inline-flex; align-items: center; background: #fff7ed; color: #c2410c; border: 1px dashed #fdba74;
  border-radius: 20px; padding: 2px 12px; font-size: .8rem;
}
</style>
